import apiClient from './apiClient';

const BASE_CONFIG = '/recepcion/turnero/config';
const BASE_TURNOS = '/recepcion/turnos';

export async function obtenerConfigTurnero() {
    const { data } = await apiClient.get(BASE_CONFIG);
    return data;
}

export async function actualizarConfigTurnero(config) {
    const { data } = await apiClient.put(BASE_CONFIG, config);
    return data;
}

const BASE_DIAS_CERRADOS = '/recepcion/turnero/dias-no-laborables';

/**
 * Los días en que el turnero no da turnos dentro de un rango: feriados de la agenda + los que
 * marcó el estudio para el turnero. Cada uno trae `origen` ('FERIADO' | 'MANUAL'), `motivo` y
 * `turnosAgendados` (los turnos que ya estaban cargados ese día, que NO se tocan).
 */
export async function listarDiasNoLaborables(desdeISO, hastaISO) {
    const { data } = await apiClient.get(BASE_DIAS_CERRADOS, { params: { desde: desdeISO, hasta: hastaISO } });
    return Array.isArray(data) ? data : [];
}

/** Solo los que marcó el estudio, de hoy en adelante: es lo que administra la configuración. */
export async function listarDiasCerradosPropios() {
    const { data } = await apiClient.get(BASE_DIAS_CERRADOS);
    return Array.isArray(data) ? data : [];
}

export async function marcarDiaNoLaborable(fechaISO, motivo) {
    const { data } = await apiClient.post(BASE_DIAS_CERRADOS, { fecha: fechaISO, motivo });
    return data;
}

export async function reabrirDiaNoLaborable(id) {
    await apiClient.delete(`${BASE_DIAS_CERRADOS}/${id}`);
}

export async function listarSemana(inicioISO) {
    const { data } = await apiClient.get(`${BASE_TURNOS}/semana`, { params: { inicio: inicioISO } });
    return Array.isArray(data) ? data : [];
}

// Salieron hacia ANSES por el plan de pago y todavía no volvieron (de cualquier día).
export async function listarVuelvenDeAnses() {
    const { data } = await apiClient.get(`${BASE_TURNOS}/vuelven-de-anses`);
    return Array.isArray(data) ? data : [];
}

// Por defecto el backend esconde los turnos cancelados hace más de 24 horas; con
// incluirCancelados vienen todos los del rango (para cuando se eligió un período a propósito).
export async function listarRango(desdeISO, hastaISO, incluirCancelados = false) {
    const params = { desde: desdeISO, hasta: hastaISO };
    if (incluirCancelados) params.incluirCancelados = true;
    const { data } = await apiClient.get(`${BASE_TURNOS}/rango`, { params });
    return Array.isArray(data) ? data : [];
}

// Turnos de una persona en TODO el historial, más recientes primero (máx. 200). Por palabras y
// sin acentos; con menos de 3 letras el backend devuelve vacío.
export async function buscarTurnosPorPersona(texto) {
    const { data } = await apiClient.get(`${BASE_TURNOS}/buscar`, { params: { q: texto } });
    return Array.isArray(data) ? data : [];
}

// Listado paginado de los turnos que sacó el bot, más nuevos primero. Devuelve
// { turnos, total, pagina, hayMas }: el total es lo que la pantalla muestra como "50 de 329".
// filtros.cliente busca en nombre+apellido; filtros.fechaCreacion es un día ("2026-08-28").
export async function obtenerTurnosChatbot(pagina = 0, tamanio = 50, filtros = {}) {
    const { cliente, fechaCreacion } = filtros;
    const { data } = await apiClient.get(`${BASE_TURNOS}/chatbot`, {
        params: {
            pagina,
            tamanio,
            ...(cliente ? { cliente } : {}),
            ...(fechaCreacion ? { fechaCreacion } : {}),
        },
    });
    return {
        turnos: Array.isArray(data?.turnos) ? data.turnos : [],
        total: data?.total ?? 0,
        pagina: data?.pagina ?? 0,
        hayMas: !!data?.hayMas
    };
}

export async function crearTurno(turno) {
    const { data } = await apiClient.post(BASE_TURNOS, turno);
    return data;
}

export async function obtenerTurno(idTurno) {
    const { data } = await apiClient.get(`${BASE_TURNOS}/${idTurno}`);
    return data;
}

export async function eliminarTurno(idTurno) {
    await apiClient.delete(`${BASE_TURNOS}/${idTurno}`);
}

export async function cambiarEstado(idTurno, estado, extra = {}) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/estado`, { estado, ...extra });
    return data;
}

// Deja una observación en el turno sin cambiarle el estado ("lo llamé y no contesta").
// Va por su propio endpoint: anotar algo no es una transición de estado.
export async function agregarObservacionTurno(idTurno, texto, idOperador) {
    const { data } = await apiClient.post(`${BASE_TURNOS}/${idTurno}/observaciones`, {
        texto,
        idOperador: idOperador != null ? String(idOperador) : null,
    });
    return data;
}

// Vuelve el turno un paso atrás (deshace ASISTIO/NO_ASISTIO/EN_CURSO/FINALIZADO marcados por error).
export async function revertirTurno(idTurno) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/revertir`);
    return data;
}

// Marca/desmarca que el cliente ya tiene carpeta (cliente existente).
export async function actualizarCarpetaTurno(idTurno, tieneCarpeta) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/carpeta`, { tieneCarpeta });
    return data;
}

// Guarda lo escrito en la ficha de atención (notas, motivo de no avance, resultado) sin cambiar
// el estado del turno, para que no se pierda al salir de la pantalla y volver.
export async function guardarBorradorAtencion(idTurno, borrador) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/borrador-atencion`, borrador);
    return data;
}

export async function editarDatosTurno(idTurno, datos) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/datos`, datos);
    return data;
}

// Reingreso de cliente: el cliente de un turno ya FINALIZADO volvió al estudio. Crea un turno nuevo
// enganchado al anterior (no lo reabre). datos = { tipoReingreso: 'SOLO_DOCUMENTACION' | 'REATENCION',
// observaciones, atencion, prioridad, idOperador?, dni?, cuilCliente? }. El exprés (SOLO_DOCUMENTACION)
// además manda documentacion, motivoExpres y motivoExpresDetalle (V249).
export async function registrarReingreso(idTurno, datos) {
    const { data } = await apiClient.post(`${BASE_TURNOS}/${idTurno}/reingreso`, datos);
    return data;
}

// Trámite exprés sin turno previo (V249): solo entrega o retira documentación y nace FINALIZADO.
// El de alguien ya atendido hoy va por registrarReingreso con tipo SOLO_DOCUMENTACION.
export async function registrarTramiteExpres(datos) {
    const { data } = await apiClient.post(`${BASE_TURNOS}/expres`, datos);
    return data;
}

export async function cambiarAtencionTurno(idTurno, atencion) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/atencion`, { atencion });
    return data;
}

// Cuántos turnos hay ya en ese bloque y cuál es el cupo. Sirve para avisar ANTES de cargar,
// sobre todo en el ingreso espontáneo, que entra siempre como sobreturno y por eso nunca
// dispara el aviso de horario completo.
export async function obtenerOcupacionBloque(fecha, horario) {
    const { data } = await apiClient.get(`${BASE_TURNOS}/ocupacion`, { params: { fecha, horario } });
    return data;
}

export async function obtenerHorariosDisponibles(fecha) {
    const { data } = await apiClient.get(`${BASE_TURNOS}/horarios-disponibles`, { params: { fecha } });
    return Array.isArray(data) ? data : [];
}

// Importación masiva del turnero histórico desde un CSV (Google Sheets exportado).
// simulacion=true no persiste nada: devuelve una previsualización del mapeo.
export async function importarTurnosCsv(archivo, simulacion = true) {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('simulacion', String(simulacion));
    const { data } = await apiClient.post(`${BASE_TURNOS}/importar-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Vínculo turno ↔ cliente del estudio (V222)
// ─────────────────────────────────────────────────────────────────────────────

// Historial de visitas de un cliente, del turno más nuevo al más viejo. Trae todos los
// estados, incluidos CANCELADO y NO_ASISTIO: que alguien haya faltado también es historia.
export async function listarTurnosPorCliente(cuil) {
    if (!cuil) return [];
    const { data } = await apiClient.get(`${BASE_TURNOS}/cliente/${cuil}`);
    return Array.isArray(data) ? data : [];
}

// Candidatos para el cartel "¿el turno pertenece a...?". No modifica nada.
export async function obtenerClienteSugeridoTurno(idTurno) {
    const { data } = await apiClient.get(`${BASE_TURNOS}/${idTurno}/cliente-sugerido`);
    return data;
}

// Vincula el turno a un cliente, o lo desvincula si cuil viene null. Desvincular queda
// registrado como decisión explícita: el matcher automático no vuelve a pisarla.
/**
 * @param criterio cómo se llegó al vínculo, para la leyenda: sin él es "a mano" (botón Vincular);
 *   la atención manda ALTA_EN_ATENCION (registró al cliente) o TRAMITE (le abrió un trámite).
 */
export async function vincularClienteTurno(idTurno, cuil, criterio) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/cliente`, { cuil: cuil || null, criterio });
    return data;
}

/** Elige el cobro que se vino a pagar, en los turnos de tipo COBRO. idCobro null desvincula. */
export async function vincularCobroTurno(idTurno, idCobro) {
    const { data } = await apiClient.patch(`${BASE_TURNOS}/${idTurno}/cobro`, { idCobro: idCobro || null });
    return data;
}

/**
 * La citación al estudio de una junta médica o pericia (turno del turnero vinculado al evento), o
 * null si todavía no se citó al cliente (V254).
 */
export async function obtenerCitacionDeEvento(idEvento) {
    const respuesta = await apiClient.get(`${BASE_TURNOS}/citacion-evento/${idEvento}`);
    return respuesta.status === 204 ? null : respuesta.data;
}
